import { useEffect, useId, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { leadsApi } from '@/lib/api';
import { Button, Field, NativeSelect, Sheet, toast } from '@elbklar/ui';
import { TextareaField, TextField } from '@/components/form/Felder';
import { formatApiError } from '@/lib/validation';
import type { AcquisitionSource, Customer } from '@/types';

const sourceOptions: { value: AcquisitionSource | ''; label: string }[] = [
  { value: '', label: 'Keine Angabe' },
  { value: 'kaltakquise', label: 'Kaltakquise' },
  { value: 'empfehlung', label: 'Empfehlung' },
  { value: 'messe', label: 'Messe' },
  { value: 'website', label: 'Website' },
  { value: 'partner', label: 'Partner' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'sonstige', label: 'Sonstige' },
];

interface FormData {
  company_name: string;
  acquisition_source: AcquisitionSource | '';
  email: string;
  phone: string;
  notes: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
}

/**
 * Das Backend legt den Ansprechpartner nur an, wenn Vor- UND Nachname gesetzt
 * sind (crm-api, routers/leads.py); sonst verwirft es ihn samt E-Mail und
 * Telefon still, die Antwort ist trotzdem 201. Darum hier eine Meldung am Feld.
 */
function nameNoetig(eigenes: string, anderer: string, werte: FormData, kompakt: boolean, fehlt: string): string | true {
  if (eigenes.trim()) return true;
  // In der Kurzform geht das Telefon auch als Firmentelefon mit, ohne Namen geht es also nicht verloren.
  const kontaktdaten = !kompakt && (werte.contact_email.trim() || werte.contact_phone.trim());
  return anderer.trim() || kontaktdaten ? fehlt : true;
}

interface LeadFormProps {
  formId: string;
  /** Kurzform fürs Handy (Schnell erfassen): Firma, Ansprechpartner, Telefon, Notiz. */
  kompakt?: boolean;
  onSuccess?: (lead: Customer) => void;
  onPendingChange?: (pending: boolean) => void;
}

export function LeadForm({ formId, kompakt = false, onSuccess, onPendingChange }: LeadFormProps) {
  const queryClient = useQueryClient();

  const { control, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      company_name: '',
      acquisition_source: '',
      email: '',
      phone: '',
      notes: '',
      contact_first_name: '',
      contact_last_name: '',
      contact_email: '',
      contact_phone: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      leadsApi.create({
        company_name: data.company_name,
        acquisition_source: data.acquisition_source || undefined,
        email: data.email || undefined,
        // Die Kurzform hat ein Telefon. Es geht als Telefon der Firma mit und,
        // mit Ansprechpartner, auch als seins: Ohne Vor- und Nachnamen legt das
        // Backend keinen an, das Telefon wäre sonst verloren.
        phone: (kompakt ? data.contact_phone : data.phone) || undefined,
        notes: data.notes || undefined,
        contact_first_name: data.contact_first_name.trim() || undefined,
        contact_last_name: data.contact_last_name.trim() || undefined,
        contact_email: data.contact_email || undefined,
        contact_phone: data.contact_phone || undefined,
      }),
    onSuccess: (lead) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Lead angelegt');
      onSuccess?.(lead);
    },
    onError: (err) => {
      toast.error(formatApiError(err) || 'Lead ließ sich nicht anlegen');
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const firma = (
    <Controller
      name="company_name"
      control={control}
      rules={{ required: 'Firma fehlt' }}
      render={({ field }) => <TextField {...field} label="Firma" required error={errors.company_name?.message} />}
    />
  );
  const ansprechpartner = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Controller
        name="contact_first_name"
        control={control}
        rules={{ validate: (v, werte) => nameNoetig(v, werte.contact_last_name, werte, kompakt, 'Vorname fehlt, ohne ihn wird kein Ansprechpartner angelegt') }}
        render={({ field }) => <TextField {...field} label="Vorname" autoComplete="off" error={errors.contact_first_name?.message} />}
      />
      <Controller
        name="contact_last_name"
        control={control}
        rules={{ validate: (v, werte) => nameNoetig(v, werte.contact_first_name, werte, kompakt, 'Nachname fehlt, ohne ihn wird kein Ansprechpartner angelegt') }}
        render={({ field }) => <TextField {...field} label="Nachname" autoComplete="off" error={errors.contact_last_name?.message} />}
      />
    </div>
  );
  const notiz = (
    <Controller
      name="notes"
      control={control}
      render={({ field }) => (
        <TextareaField {...field} label="Notizen" rows={3} placeholder="Interesse, Kontext, nächste Schritte" />
      )}
    />
  );

  if (kompakt) {
    return (
      <form id={formId} onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
        {firma}
        <div>
          <h3 className="mb-3 t-body-strong">Ansprechpartner</h3>
          {ansprechpartner}
        </div>
        <Controller
          name="contact_phone"
          control={control}
          render={({ field }) => <TextField {...field} label="Telefon" type="tel" inputMode="tel" />}
        />
        {notiz}
      </form>
    );
  }

  return (
    <form id={formId} onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
      {firma}

      <Controller
        name="acquisition_source"
        control={control}
        render={({ field }) => (
          <Field label="Quelle">{(f) => <NativeSelect {...f} {...field} options={sourceOptions} />}</Field>
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          name="email"
          control={control}
          render={({ field }) => <TextField {...field} label="E-Mail" type="email" />}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => <TextField {...field} label="Telefon" type="tel" />}
        />
      </div>

      {/* Ansprechpartner direkt mit anlegen */}
      <div className="border-t border-border pt-4">
        <h3 className="mb-3 t-body-strong">Ansprechpartner (optional)</h3>
        {ansprechpartner}
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="contact_email"
            control={control}
            render={({ field }) => <TextField {...field} label="E-Mail" type="email" />}
          />
          <Controller
            name="contact_phone"
            control={control}
            render={({ field }) => <TextField {...field} label="Telefon" type="tel" />}
          />
        </div>
      </div>

      {notiz}
    </form>
  );
}

interface LeadSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kompakt?: boolean;
  onCreated?: (lead: Customer) => void;
}

/** Lead anlegen: am Handy als Sheet von unten, am Rechner als Dialog. */
export function LeadSheet({ open, onOpenChange, kompakt, onCreated }: LeadSheetProps) {
  const formId = useId();
  const [speichert, setSpeichert] = useState(false);
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Lead anlegen"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={speichert}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={speichert}>
            Lead anlegen
          </Button>
        </>
      }
    >
      <LeadForm
        formId={formId}
        kompakt={kompakt}
        onPendingChange={setSpeichert}
        onSuccess={(lead) => {
          onOpenChange(false);
          onCreated?.(lead);
        }}
      />
    </Sheet>
  );
}
